<template>
    <div>
        <div v-if="!selectedDocument" class="document-search-list">
            <div class="filter-bar">
                <common-icon class="search-icon" name="search" size="small" @click="getDocuments(doc)"></common-icon>

                <input
                    v-model="searchText"
                    type="text"
                    class="search-input"
                    :placeholder="$t('forms.view_document.fields.search.placeholder')"
                    @input="onSearch"
                />
            </div>
            <div class="tab-filter">
                <select-input
                    class="schedule-apply"
                    :placeholder="$t('forms.view_document.fields.type.placeholder')"
                    :hasAutofocus="true"
                    :options="typeOptions"
                    :value="recordTypeId"
                    @change="changeTypeId($event)"
                ></select-input>

                <select-input
                    class="schedule-apply"
                    :placeholder="$t('forms.view_document.fields.source.placeholder')"
                    :hasAutofocus="true"
                    :options="sourceOptions"
                    :value="sourceId"
                    @change="changeSourceId($event)"
                ></select-input>
            </div>
        </div>

        <div v-if="!selectedDocument" class="document-list">
            <template v-if="isLoading">
                <div class="loading-spinner">
                    <span>{{ $t('modals.import_contacts_modal.loading') }}</span>
                    <common-logo-spinner :width="56"></common-logo-spinner>
                </div>
            </template>
            <div v-else>
                <div class="card-header">
                    <div style="display: flex; align-items: center; gap: 16px">
                        <h3 class="card-title">
                            {{ $t('modals.patient_profile.profile.detail_form.card_head', { count: totalCount }) }}
                        </h3>
                        <common-button
                            :label="$t('modals.patient_profile.profile.detail_form.shared_links_button')"
                            type="secondary"
                            size="small"
                            @click="openSharedLinksModal"
                        ></common-button>
                    </div>
                    <div style="display: flex; align-items: center">
                        <label v-if="documents.length" class="select-all">
                            <input type="checkbox" :checked="isAllSelected" @change="toggleSelectAll($event)" />
                            {{ $t('modals.patient_profile.profile.detail_form.select_all') }}
                        </label>
                        <common-button
                            v-if="hasSelectedDocuments"
                            :label="$t('modals.patient_profile.profile.detail_form.share_button')"
                            type="primary"
                            size="small"
                            @click="openShareModal"
                        ></common-button>
                    </div>
                </div>
                <div v-for="doc in documents" :key="doc.id" class="document-card">
                    <div class="doc-info">
                        <input
                            type="checkbox"
                            class="input-checkbox"
                            :checked="doc.selected"
                            @change="toggleDocumentSelected(doc)"
                        />
                        <div class="doc-icon">{{ doc.icon }}</div>
                        <div>
                            <div class="doc-title">{{ doc.title }}</div>
                            <div class="doc-meta">
                                <span class="doc-meta__item">
                                    <common-icon name="timeline" size="tiny-small"></common-icon>
                                    <span>{{ doc.date }}</span>
                                </span>
                                <span class="doc-meta__item">
                                    <common-icon name="user" size="tiny-small"></common-icon>
                                    <span>{{ doc.provider }}</span>
                                </span>
                                <span class="doc-meta__item">
                                    <span>{{ doc.size }}</span>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="doc-actions">
                        <div class="doc-tag" @click="viewDocument(doc.id)">{{ doc.type }}</div>
                        <div class="doc-btns">
                            <div class="doc-btn" @click="viewDocument(doc.id)">
                                <common-icon name="view" size="small"></common-icon>
                            </div>
                            <div v-if="isDocumentOwner(doc)" class="doc-btn" @click="deleteDocument(doc)">
                                <common-icon name="trash-bin" size="small" class="doc-delete-icon"></common-icon>
                            </div>
                            <div class="doc-btn" @click="downloadDocument(doc)">
                                <common-icon name="download" size="small"></common-icon>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <common-buttons v-if="!selectedDocument && !isLoading && documents.length > 0 && documents.length < totalCount">
            <common-button
                :label="$t('pages.physicians.show.buttons.view_more')"
                type="primary"
                @click="viewMore"
            ></common-button>
        </common-buttons>

        <div v-if="selectedDocument" class="detail-view">
            <div v-if="!isEditBtnClicked">
                <div class="detail-header">
                    <h3>{{ selectedDocument?.fileName }}</h3>
                    <div class="edit-download">
                        <button v-if="showEditBtn" class="btn" @click="allowEdit">
                            <common-icon name="edit" size="small"></common-icon>
                            {{ $t('modals.patient_profile.profile.detail_form.button.edit') }}
                        </button>
                        <button class="btn" @click="downloadDocument(selectedDocument)">
                            <common-icon name="download" size="small"></common-icon>
                            {{ $t('modals.patient_profile.profile.detail_form.button.title') }}
                        </button>
                    </div>
                </div>

                <div class="detail-body">
                    <div class="preview-box">
                        <template v-if="isPreviewLoading">
                            <div class="loading-spinner">
                                <span>{{ $t('modals.import_contacts_modal.loading') }}</span>
                                <common-logo-spinner :width="56"></common-logo-spinner>
                            </div>
                        </template>
                        <iframe v-else-if="isPdf && fileBlobUrl" :src="fileBlobUrl" class="pdf-viewer" frameborder="0"></iframe>
                        <img v-else-if="isImage && fileBlobUrl" :src="fileBlobUrl" class="image-viewer" />
                        <div v-else-if="isSpreadsheet && spreadsheetRows.length" class="excel-preview-wrapper">
                            <div class="excel-header-bar">
                                <span class="excel-file-badge">
                                    <common-icon name="file" size="tiny-small"></common-icon>
                                    {{ selectedDocument.fileName }}
                                </span>
                                <span class="excel-grid-meta">
                                    {{ spreadsheetRows.length }} rows • {{ spreadsheetMaxCols }} columns
                                </span>
                            </div>
                            <div class="excel-table-container">
                                <table class="excel-table">
                                    <thead>
                                        <tr>
                                            <th class="excel-corner-cell"></th>
                                            <th v-for="colIdx in spreadsheetMaxCols" :key="colIdx" class="excel-col-header">
                                                {{ getColumnLetter(colIdx - 1) }}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(row, rowIdx) in spreadsheetRows" :key="rowIdx">
                                            <td class="excel-row-header">{{ rowIdx + 1 }}</td>
                                            <td
                                                v-for="(cell, colIdx) in row"
                                                :key="colIdx"
                                                class="excel-cell"
                                                :title="cell !== null && cell !== undefined ? String(cell) : ''"
                                            >
                                                {{ cell }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div v-if="sheetNames.length > 1" class="excel-sheet-tabs">
                                <button
                                    v-for="sheetName in sheetNames"
                                    :key="sheetName"
                                    type="button"
                                    class="excel-sheet-tab"
                                    :class="{ 'excel-sheet-tab--active': activeSheet === sheetName }"
                                    @click="changeSheet(sheetName)"
                                >
                                    {{ sheetName }}
                                </button>
                            </div>
                        </div>
                        <div v-else-if="isSpreadsheet && textContent" class="text-preview-wrapper">
                            <div class="text-header-bar">
                                <span class="text-file-badge">
                                    <common-icon name="file" size="tiny-small"></common-icon>
                                    {{ selectedDocument.fileName }}
                                </span>
                                <div class="text-header-actions">
                                    <span class="text-grid-meta">
                                        {{ textLines.length }} {{ textLines.length === 1 ? 'line' : 'lines' }} •
                                        {{ textContent.length }} characters
                                    </span>
                                </div>
                            </div>
                            <div class="text-editor-body">
                                <div class="text-line-numbers">
                                    <div v-for="lineNum in textLines.length" :key="lineNum" class="text-line-number">
                                        {{ lineNum }}
                                    </div>
                                </div>
                                <pre class="text-editor-content">{{ textContent }}</pre>
                            </div>
                        </div>
                        <div v-else class="preview-placeholder">
                            <common-icon name="file" size="extra-large"></common-icon>
                            <p>{{ $t('modals.patient_profile.profile.detail_form.card_heading') }}</p>
                            <span>{{ selectedDocument.fileName }} • {{ formatFileSize(selectedDocument.size) }}</span>
                            <a href="#" @click.prevent="downloadDocument(selectedDocument)">
                                {{ $t('common.images.download') }}
                            </a>
                        </div>
                    </div>

                    <div class="doc-details">
                        <h4>{{ $t('modals.patient_profile.profile.detail_form.document_detail.title') }}</h4>
                        <p>
                            <span class="data">{{ $t('modals.patient_profile.profile.detail_form.document_detail.type') }}</span>
                            <span class="doc-tag">{{ selectedDocument?.recordType?.name }}</span>
                        </p>
                        <p>
                            <span class="data">{{ $t('modals.patient_profile.profile.detail_form.document_detail.date') }}</span>
                            {{ dateFormate(selectedDocument.date) }}
                        </p>
                        <p>
                            <span class="data">
                                {{ $t('modals.patient_profile.profile.detail_form.document_detail.provider') }}
                            </span>
                            {{ selectedDocument?.source?.name }}
                        </p>
                        <p>
                            <span class="data">{{ $t('modals.patient_profile.profile.detail_form.document_detail.size') }}</span>
                            {{ formatFileSize(selectedDocument.size) }}
                        </p>
                        <h4>{{ $t('modals.patient_profile.profile.detail_form.document_detail.description') }}</h4>
                        <p>{{ selectedDocument?.note }}</p>
                        <common-divider></common-divider>
                        <button class="back-btn" @click="selectedDocument = null">
                            {{ $t('modals.patient_profile.profile.detail_form.document_detail.button.title') }}
                        </button>
                    </div>
                </div>
            </div>
            <div v-else>
                <form-wrapper>
                    <form-row>
                        <form-cell width="half">
                            <label class="form-field__label">
                                {{ $t('forms.patient_profile.fields.profile.fileName.label') }}
                            </label>
                            <text-field
                                :placeholder="$t('forms.patient_profile.fields.profile.fileName.placeholder')"
                                :isRequired="true"
                                :formData="data"
                                valuePath="fileName"
                                @change="change($event)"
                            ></text-field>
                        </form-cell>
                        <form-cell width="half">
                            <label class="form-field__label">
                                {{ $t('forms.patient_profile.fields.profile.document.label') }}
                            </label>
                            <text-field
                                :placeholder="$t('forms.patient_profile.fields.profile.document.placeholder')"
                                :isRequired="true"
                                type="date"
                                :formData="data"
                                valuePath="date"
                                @change="change($event)"
                            ></text-field>
                        </form-cell>
                    </form-row>
                    <form-row>
                        <form-cell width="half">
                            <label class="form-field__label">
                                {{ $t('forms.patient_profile.fields.profile.label') }}
                            </label>
                            <select-input
                                class="schedule-apply__input"
                                :placeholder="$t('forms.patient_profile.fields.profile.placeholder')"
                                :hasAutofocus="true"
                                :options="documentOptions"
                                :formData="data"
                                :value="data.recordType.id"
                                @change="changeCurrentDocumentId($event)"
                            ></select-input>
                        </form-cell>
                        <form-cell width="half">
                            <label class="form-field__label">
                                {{ $t('forms.patient_profile.fields.profile.provider.label') }}
                            </label>
                            <div :class="searchAutocompleteClass">
                                <div :class="iconClass">
                                    <common-icon :name="iconName" size="large"></common-icon>
                                </div>

                                <input
                                    v-model="searchTerm"
                                    type="text"
                                    class="search-autocomplete__input"
                                    :placeholder="$t('forms.patient_profile.fields.profile.provider.placeholder')"
                                    :disabled="!!selectedUser"
                                    @keyup="inputChange"
                                    @focus="inputFocus"
                                    @blur="inputBlur"
                                />
                                <div v-if="!!items.length" class="search-autocomplete__suggestions">
                                    <div v-for="(item, index) in items" :key="index">
                                        <single-item
                                            :disableNetworkPlan="disableNetworkPlan"
                                            :item="item"
                                            @make-referral="userSelected($event)"
                                        ></single-item>
                                    </div>
                                </div>
                                <common-user-label
                                    v-if="selectedUser"
                                    class="search-autocomplete__user-label"
                                    :user="selectedUser"
                                    :isDisabled="isDisabled"
                                    @close="removeSelectedUser()"
                                ></common-user-label>
                            </div>
                        </form-cell>
                    </form-row>

                    <form-row>
                        <form-cell width="half">
                            <label class="form-field__label">
                                {{ $t('forms.patient_profile.fields.profile.comment.label') }}
                            </label>
                            <text-field
                                :placeholder="$t('forms.patient_profile.fields.profile.comment.placeholder')"
                                :isRequired="true"
                                :formData="data"
                                valuePath="note"
                                @change="change($event)"
                            ></text-field>
                        </form-cell>
                    </form-row>
                </form-wrapper>
                <common-buttons style="margin-top: 20px">
                    <common-button
                        :isSubmittable="false"
                        :label="$t('pages.patient.details.buttons.secondary')"
                        type="secondary"
                        @click="disAllowEdit"
                    ></common-button>
                    <common-button
                        :isProcessing="isSending"
                        :label="$t('pages.patient.details.buttons.primary')"
                        type="primary"
                        @click="updateDocument"
                    ></common-button>
                    <common-button
                        :isSubmittable="false"
                        :label="$t('modals.patient_profile.profile.detail_form.document_detail.button.title')"
                        type="secondary"
                        @click="backToDocuments"
                    ></common-button>
                </common-buttons>
            </div>
        </div>
        <common-notification ref="commonSuccessNotificationChild" :content="notificationMessage"></common-notification>

        <common-modal
            v-if="showDeleteModal"
            :heading="$t('modals.patient_profile.profile.detail_form.delete_modal.heading')"
            width="small"
            @close="cancelDelete"
        >
            <div class="delete-modal-body">
                <p>{{ $t('modals.patient_profile.profile.detail_form.delete_modal.message') }}</p>
                <common-buttons align="center">
                    <common-button
                        :isSubmittable="false"
                        :label="$t('modals.patient_profile.profile.detail_form.delete_modal.cancel')"
                        type="secondary"
                        @click="cancelDelete"
                    ></common-button>
                    <common-button
                        :isProcessing="isDeleting"
                        :label="$t('modals.patient_profile.profile.detail_form.delete_modal.confirm')"
                        type="primary"
                        @click="confirmDelete"
                    ></common-button>
                </common-buttons>
            </div>
        </common-modal>

        <common-modal
            v-if="showSharedLinksModal"
            :heading="$t('modals.patient_profile.profile.detail_form.shared_links.heading')"
            width="small"
            @close="showSharedLinksModal = false"
        >
            <div class="share-modal-body">
                <common-logo-spinner v-if="isLoadingShares" :width="56"></common-logo-spinner>
                <p v-else-if="!documentShares.length" class="modal-subtitle">
                    {{ $t('modals.patient_profile.profile.detail_form.shared_links.empty') }}
                </p>
                <div v-for="share in documentShares" v-else :key="share.id" class="shared-link-row">
                    <div>
                        <strong>
                            {{ formatShareRecipient(share) }}
                        </strong>
                        <div class="modal-subtitle">
                            {{
                                $t('modals.patient_profile.profile.detail_form.shared_links.documents', {
                                    count: share.documentCount,
                                })
                            }}
                            ·
                            {{
                                $t('modals.patient_profile.profile.detail_form.shared_links.status_' + share.status.toLowerCase())
                            }}
                            ·
                            {{
                                $t('modals.patient_profile.profile.detail_form.shared_links.expires', {
                                    date: formatShareDate(share.expiresAt),
                                })
                            }}
                        </div>
                    </div>
                    <common-button
                        v-if="share.status === 'ACTIVE'"
                        :isProcessing="revokingShareId === share.id"
                        :label="$t('modals.patient_profile.profile.detail_form.shared_links.revoke')"
                        type="secondary"
                        size="small"
                        @click="revokeShare(share)"
                    ></common-button>
                </div>
            </div>
        </common-modal>

        <share-document-modal
            v-if="showShareModal"
            :selectedDocuments="selectedDocuments"
            :patientId="patientId"
            @close="closeShareModal"
            @shared="loadDocumentShares"
        ></share-document-modal>
    </div>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import HandleRequestMixin from 'mixins/HandleRequestMixin'
    import PatientAPI from 'api/PatientAPI'
    import FileMixin from 'mixins/FileMixin'
    import config from '../../../config'
    import debounce from 'lodash.debounce'
    import SingleItem from 'components/page-elements/search-autocomplete/SearchProviderAutocompleteSingleItem'
    import ShareDocumentModal from './ShareDocumentModal'
    import { getRecipientLabel } from '@/enums/RecordShareRecipientTypeEnum'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import { dateFormate } from '~/helpers'
    import dayjs from 'dayjs'
    import * as XLSX from 'xlsx'

    export default {
        mixins: [FormMixin, HandleRequestMixin, FileMixin],
        components: {
            SingleItem,
            ShareDocumentModal,
        },
        props: {
            documentOptions: { type: Array, default: () => [] },
            iconPosition: { type: String, default: 'left' },
            disableNetworkPlan: { type: Boolean, default: false },
            icon: { type: String, default: 'member' },
            isDisabled: { type: Boolean, default: false },
        },
        data() {
            return {
                patientId: null,
                selectedDocument: null,
                recordTypeId: 0,
                sourceId: 0,
                sourceOptions: [],
                documents: [],
                totalCount: 0,
                searchText: '',
                offset: 0,
                limit: 10,
                isLoading: false,
                images: [],
                selectedDocuments: [],
                fileBlobUrl: null,
                fileExtension: null,
                textContent: '',
                sheetNames: [],
                activeSheet: '',
                spreadsheetRows: [],
                spreadsheetMaxCols: 0,
                workbook: null,
                isCopied: false,
                isPreviewLoading: false,
                isEditBtnClicked: false,
                searchTerm: '',
                selectedUser: null,
                isFocused: false,
                items: [],
                data: {
                    fileName: '',
                    date: '',
                    recordType: null,
                    source: null,
                    note: '',
                },
                notificationMessage: '',
                isSending: false,
                showDeleteModal: false,
                documentToDelete: null,
                isDeleting: false,
                showShareModal: false,
                showSharedLinksModal: false,
                documentShares: [],
                isLoadingShares: false,
                revokingShareId: null,
            }
        },

        computed: {
            searchAutocompleteClass() {
                return {
                    [`search-autocomplete__search-input--${this.iconPosition}`]: true,
                    'search-autocomplete__search-input': true,
                    'form-input-text': true,
                }
            },
            iconName() {
                if (this.iconBackground === 'primary' && this.isFocused) return `${this.icon}-white`
                return `${this.icon}-gray`
            },
            iconClass() {
                return {
                    'search-autocomplete__icon': true,
                    [`search-autocomplete__icon--${this.iconPosition}`]: true,
                    [`search-autocomplete__icon--color-${this.iconBackground}`]: this.isFocused,
                }
            },
            isAllSelected() {
                return this.documents.length > 0 && this.documents.every((doc) => doc.selected)
            },
            isPdf() {
                return this.fileExtension === 'pdf'
            },
            isImage() {
                return ['jpg', 'jpeg', 'png'].includes(this.fileExtension)
            },
            isSpreadsheet() {
                return this.fileExtension === 'csv'
            },
            textLines() {
                if (!this.textContent) return []
                return this.textContent.split('\n')
            },
            typeOptions() {
                return [{ label: this.$t('modals.patient_profile.profile.type.text'), value: 0 }, ...this.documentOptions]
            },
            hasSelectedDocuments() {
                return this.selectedDocuments.length > 0
            },
            showEditBtn() {
                return this.$auth.getPractice().practiceType === PracticeTypeEnum.ANCILLARY
                    ? this.$auth.getPractice().id === this.selectedDocument.fromPracticeId
                    : this.$auth.getActiveMember().id === this.selectedDocument.fromProviderId
            },
            activeMemberId() {
                return this.$auth.getActiveMember().id
            },
        },
        methods: {
            dateFormate,
            extractProviderName(sourceName) {
                if (!sourceName) return ''
                return sourceName.split(',')[0].trim()
            },
            changeCurrentDocumentId(val) {
                this.recordTypeId = val
                const matched = this.documentOptions.find((opt) => Number(opt.value) === Number(val))
                if (matched) {
                    this.data.recordType = {
                        id: matched.value,
                        name: matched.label,
                    }
                }
            },

            inputChange: debounce(function () {
                this.emitEvent({ name: 'input-change' })
                this.fetchProviders({
                    searchTerm: this.searchTerm,
                    includeTyped: true,
                    autoSelect: false,
                })
            }, config.DEBOUNCE.DEFAULT),

            userSelected(event) {
                if (event?.type === 'typed') return
                this.searchTerm = ''
                this.items = []
                this.selectedUser = event
                this.emitEvent({ name: 'user-select', payload: event })
            },

            removeSelectedUser() {
                this.selectedUser = null
                this.emitEvent({ name: 'user-select', payload: null })
            },

            inputFocus() {
                this.isFocused = true
            },

            inputBlur() {
                this.isFocused = false
            },

            changeTypeId(val) {
                this.recordTypeId = Number(val)
                this.offset = 0
                this.documents = []
                this.getDocuments()
            },

            changeSourceId(val) {
                this.sourceId = Number(val)
                this.offset = 0
                this.documents = []
                this.getDocuments()
            },

            getSources() {
                PatientAPI.getSources(this.patientId).then((res) => {
                    this.sourceOptions = [
                        { label: this.$t('modals.patient_profile.profile.source.text'), value: 0 },
                        ...res.map((item) => ({
                            label: item.name,
                            value: item.id,
                        })),
                    ]
                })
            },

            getDocuments(append = false) {
                this.isLoading = true
                return PatientAPI.getDocuments(this.patientId, {
                    limit: this.limit,
                    offset: this.offset,
                    recordTypeId: this.recordTypeId || undefined,
                    sourceId: this.sourceId || undefined,
                    search: this.searchText || undefined,
                })
                    .then((res) => {
                        this.totalCount = Number(res._headers['x-total-count'])
                        const mapped = res.map((item) => {
                            return {
                                id: item.id,
                                title: item.fileName,
                                date: dateFormate(item.date),
                                provider: item?.source?.name || 'Unknown',
                                size: this.formatFileSize(item.size),
                                type: item?.recordType?.name || 'Unknown',
                                icon: this.getDocIcon(item?.recordType?.id),
                                description: item.note || 'No description available',
                                url: item.url,
                                fromProviderId: item.fromProviderId,
                                selected: false,
                                isExpired: item.isExpired || false,
                                daysLeft:
                                    item.expiresAt && !item.isExpired
                                        ? Math.max(0, dayjs(item.expiresAt).diff(dayjs(), 'day'))
                                        : null,
                            }
                        })
                        if (append) {
                            this.documents = [...this.documents, ...mapped]
                        } else {
                            this.documents = mapped
                        }
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            },

            formatFileSize(kb) {
                if (!kb) return '0 KB'
                if (kb < 1024) return `${kb} KB`
                return `${(kb / 1024).toFixed(1)} MB`
            },

            getDocIcon(type) {
                switch (type) {
                    case 1:
                        return '🧪'
                    case 2:
                        return '📷'
                    case 3:
                        return '👩‍⚕️'
                    case 4:
                        return '💊'
                    default:
                        return '📄'
                }
            },

            onSearch() {
                clearTimeout(this._searchDelay)
                this._searchDelay = setTimeout(() => {
                    this.offset = 0
                    this.documents = []
                    this.getDocuments()
                }, 400)
            },

            viewMore() {
                this.offset = this.offset + this.limit
                this.getDocuments(true)
            },

            getExtension(document) {
                const fileName =
                    typeof document === 'object'
                        ? document?.fileName || document?.name || document?.title || document?.url || ''
                        : document || ''
                const clean = fileName.split('?')[0].split('#')[0]
                if (clean.includes('.')) {
                    return clean.split('.').pop().toLowerCase()
                }
                return ''
            },

            getMimeType(extension) {
                const map = {
                    pdf: 'application/pdf',
                    png: 'image/png',
                    jpg: 'image/jpeg',
                    jpeg: 'image/jpeg',
                    csv: 'text/csv',
                }
                return map[extension] || ''
            },

            copyTextContent() {
                if (!this.textContent) return
                if (navigator.clipboard) {
                    navigator.clipboard.writeText(this.textContent).then(() => {
                        this.isCopied = true
                        setTimeout(() => {
                            this.isCopied = false
                        }, 2000)
                    })
                }
            },

            getColumnLetter(colIndex) {
                let letter = ''
                let temp = colIndex
                while (temp >= 0) {
                    letter = String.fromCharCode((temp % 26) + 65) + letter
                    temp = Math.floor(temp / 26) - 1
                }
                return letter
            },

            changeSheet(sheetName) {
                this.activeSheet = sheetName
                this.renderActiveSheet()
            },

            renderActiveSheet() {
                if (!this.workbook || !this.activeSheet) {
                    this.spreadsheetRows = []
                    this.spreadsheetMaxCols = 0
                    return
                }
                const worksheet = this.workbook.Sheets[this.activeSheet]
                if (!worksheet) {
                    this.spreadsheetRows = []
                    this.spreadsheetMaxCols = 0
                    return
                }
                const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' })
                let maxCols = 0
                rawData.forEach((row) => {
                    if (Array.isArray(row) && row.length > maxCols) {
                        maxCols = row.length
                    }
                })
                this.spreadsheetMaxCols = maxCols
                this.spreadsheetRows = rawData.map((row) => {
                    const padded = [...(row || [])]
                    while (padded.length < maxCols) {
                        padded.push('')
                    }
                    return padded
                })
            },

            viewDocument(documentId, isExpired) {
                if (isExpired) return
                this.fileBlobUrl = null
                this.fileExtension = null
                this.textContent = ''
                this.sheetNames = []
                this.activeSheet = ''
                this.spreadsheetRows = []
                this.spreadsheetMaxCols = 0
                this.workbook = null
                this.isPreviewLoading = true

                PatientAPI.getSingleDocument(this.patientId, documentId).then((res) => {
                    this.selectedDocument = res
                    this.fetchFile(res)
                })
            },
            fetchFile(document) {
                if (this.fileBlobUrl) {
                    URL.revokeObjectURL(this.fileBlobUrl)
                }

                this.fileBlobUrl = null
                this.fileExtension = this.getExtension(document)
                this.textContent = ''
                this.sheetNames = []
                this.activeSheet = ''
                this.spreadsheetRows = []
                this.spreadsheetMaxCols = 0
                this.workbook = null
                this.isPreviewLoading = true

                this.fetchPrivateBlob(document.url)
                    .then(async (blob) => {
                        if (this.isSpreadsheet) {
                            try {
                                const arrayBuffer = await blob.arrayBuffer()
                                const workbook = XLSX.read(arrayBuffer, { type: 'array' })
                                this.workbook = workbook
                                this.sheetNames = workbook.SheetNames || []
                                this.activeSheet = this.sheetNames[0] || ''
                                this.renderActiveSheet()
                            } catch (e) {
                                try {
                                    this.textContent = await blob.text()
                                } catch {
                                    this.textContent = ''
                                }
                            }
                        }
                        const mimeType = this.getMimeType(this.fileExtension)
                        const finalBlob =
                            mimeType && (!blob.type || blob.type === 'application/octet-stream')
                                ? new Blob([blob], { type: mimeType })
                                : blob
                        this.fileBlobUrl = URL.createObjectURL(finalBlob)
                    })
                    .catch(() => {
                        this.fileBlobUrl = null
                        this.textContent = ''
                    })
                    .finally(() => {
                        this.isPreviewLoading = false
                    })
            },
            downloadDocument(document) {
                if (!document?.url) {
                    return
                }
                const fileName = document.fileName || document.title
                this.downloadFile(document.url, fileName)
            },

            allowEdit() {
                const doc = this.selectedDocument
                this.data = {
                    fileName: doc.fileName,
                    date: doc.date?.split('T')[0],
                    recordType: doc.recordType,
                    source: doc.source,
                    note: doc.note,
                }
                const providerName = this.extractProviderName(doc?.source?.name)
                this.searchTerm = providerName
                this.fetchProviders({
                    searchTerm: providerName,
                    autoSelect: true,
                    includeTyped: false,
                })
                this.isEditBtnClicked = true
            },
            disAllowEdit() {
                this.isEditBtnClicked = false
                this.data = {
                    fileName: '',
                    date: '',
                    recordType: null,
                    source: null,
                    note: '',
                }
            },
            backToDocuments() {
                ;(this.selectedDocument = null), this.disAllowEdit()
            },
            fetchProviders({ searchTerm, autoSelect = false, includeTyped = false }) {
                if (!searchTerm || searchTerm.length < 2) {
                    this.items = []
                    return
                }

                return PatientAPI.getProviderFromNetwork(searchTerm, 5, 0).then((results) => {
                    if (!results?.length) {
                        this.items = []
                        this.emitEvent?.({
                            name: 'no-results',
                            payload: { searchTerm },
                        })
                        return
                    }

                    if (autoSelect) {
                        const matched = results.find((item) => {
                            return (
                                +item.id === +this.selectedDocument.fromProviderId ||
                                item.name.toLowerCase() === searchTerm.toLowerCase()
                            )
                        })

                        if (matched) {
                            this.selectedUser = matched
                            this.items = []
                            this.searchTerm = ''
                            return
                        }
                    }

                    let finalItems = [...results]

                    if (includeTyped) {
                        finalItems.unshift({
                            name: searchTerm,
                            type: 'typed',
                        })
                    }

                    this.items = finalItems
                })
            },
            updateDocument() {
                this.isSending = true
                this.notificationMessage = ''
                const doc = this.selectedDocument
                const activeMember = this.$auth.getActiveMember()

                let sourcePracticeId = doc.sourcePracticeId || null
                let sourceUserId = doc.sourceUserId || null
                let sourceName = doc.source?.name || ''

                if (this.selectedUser) {
                    if (this.selectedUser.type === 'user') {
                        sourcePracticeId = this.selectedUser.practice?.id
                        sourceUserId = this.selectedUser.id
                    }

                    if (this.selectedUser.type === 'practice') {
                        sourcePracticeId = this.selectedUser.id
                        sourceUserId = null
                    }

                    sourceName = this.selectedUser.designation
                        ? `${this.selectedUser.name}, ${this.selectedUser.designation}`
                        : this.selectedUser.name
                }

                const payload = {
                    documentId: doc.id,
                    patientId: Number(this.patientId),
                    fileName: this.data.fileName || doc.fileName,
                    recordTypeId: this.recordTypeId || doc.recordType?.id,
                    date: this.data.date || doc.date?.split('T')[0],
                    sourceName,
                    sourcePracticeId,
                    sourceUserId,
                    note: this.data.note ?? doc.note,
                    activeMemberId: activeMember.id,
                }

                try {
                    PatientAPI.updateDocuments(payload).then(() => {
                        this.notificationMessage = this.$t('pages.patient.profile.notifications.update_success')
                        this.$refs.commonSuccessNotificationChild.showNotification()

                        this.disAllowEdit()
                        this.selectedDocument = null
                        this.selectedUser = null
                        this.searchTerm = ''
                        this.offset = 0
                        this.documents = []
                        this.getDocuments()
                    })
                } finally {
                    this.isSending = false
                }
            },
            isDocumentOwner(doc) {
                return doc.fromProviderId === this.activeMemberId
            },

            toggleDocumentSelected(doc) {
                doc.selected = !doc.selected
                this.selectedDocuments = this.documents.filter((d) => d.selected)
            },

            toggleSelectAll(event) {
                const checked = event.target.checked
                this.documents.forEach((doc) => {
                    doc.selected = checked
                })
                this.selectedDocuments = checked ? [...this.documents] : []
            },

            openShareModal() {
                this.shareRecipientEmail = ''
                this.shareLink = ''
                this.shareCopiedMessage = ''
                this.showShareModal = true
            },

            openSharedLinksModal() {
                this.showSharedLinksModal = true
                this.loadDocumentShares()
            },

            loadDocumentShares() {
                this.isLoadingShares = true
                return PatientAPI.getDocumentShares(this.patientId)
                    .then((res) => {
                        this.documentShares = res
                    })
                    .finally(() => {
                        this.isLoadingShares = false
                    })
            },

            revokeShare(share) {
                if (!window.confirm(this.$t('modals.patient_profile.profile.detail_form.shared_links.revoke_confirm'))) {
                    return
                }
                this.revokingShareId = share.id
                PatientAPI.revokeDocumentShare(share.id)
                    .then(() => this.loadDocumentShares())
                    .finally(() => {
                        this.revokingShareId = null
                    })
            },

            formatShareDate(date) {
                return dayjs(date).format('MMM D, YYYY')
            },

            closeShareModal() {
                this.showShareModal = false
            },

            formatShareRecipient(share) {
                const typeLabel = share.recipientType ? getRecipientLabel(share.recipientType) : ''

                if (typeLabel && share.recipientEmail) {
                    return `${typeLabel} (${share.recipientEmail})`
                }
                if (typeLabel) {
                    return typeLabel
                }
                return share.recipientEmail || this.$t('modals.patient_profile.profile.detail_form.shared_links.link_only')
            },

            deleteDocument(doc) {
                this.documentToDelete = doc
                this.showDeleteModal = true
            },

            cancelDelete() {
                this.showDeleteModal = false
                this.documentToDelete = null
            },

            confirmDelete() {
                if (!this.documentToDelete) return
                this.isDeleting = true

                PatientAPI.deleteDocument(this.documentToDelete.id, this.activeMemberId)
                    .then(() => {
                        const id = this.documentToDelete.id
                        this.documents = this.documents.filter((d) => d.id !== id)
                        this.selectedDocuments = this.selectedDocuments.filter((d) => d.id !== id)
                        this.totalCount = Math.max(0, this.totalCount - 1)
                        this.notificationMessage = this.$t('modals.patient_profile.profile.detail_form.delete_success')
                        this.$refs.commonSuccessNotificationChild.showNotification()
                        this.cancelDelete()
                    })
                    .catch(() => {
                        this.notificationMessage = this.$t('modals.patient_profile.profile.detail_form.delete_error')
                        this.$refs.commonSuccessNotificationChild.showNotification()
                    })
                    .finally(() => {
                        this.isDeleting = false
                    })
            },
        },
        mounted() {
            this.patientId = this.$route.params.id
            this.getDocuments().then(() => {
                this.$emit('documents-loaded')
                this.getSources()
            })
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';
    .loading-spinner {
        text-align: center;
        font-size: 16px;
    }
    .document-search-list {
        display: flex;
        margin-bottom: 20px;
        padding: 20px 28px;
        border-radius: 12px;
        border: 1px solid #ddd;
        background-color: #fff;
    }
    .filter-bar {
        position: relative;
        width: 100%;
        display: flex;
        align-items: center;
        i {
            color: #99a1af;
            top: 24px;
            left: 16px;
        }
    }

    .search-input {
        width: 100%;
        height: 40px;
        padding: 8px 16px 8px 40px;
        border: 1px solid #f5f5f5;
        border-radius: 10px;
        background: #f5f5f5;
        font-size: 12px;
        outline: none;

        &:focus {
            border-color: #d9d9d9;
            background: #fff;
        }
    }

    .search-icon {
        position: absolute;
        left: 14px;
        top: 50%;
        transform: translateY(-50%);
        pointer-events: none;
        z-index: 1;
    }
    .schedule-apply {
        width: 200px;
        margin-left: 20px;
        padding: 0;
    }
    .download-print-btns {
        margin-bottom: 10px;
    }
    .input-checkbox {
        cursor: pointer;
        width: 14px;
        height: 14px;
        margin-left: 18px;
    }
    .expired-badge {
        font-size: 10px;
        color: #e53935;
        background: #ffebee;
        padding: 2px 6px;
        border-radius: 4px;
        font-weight: bold;
        text-transform: uppercase;
        margin-right: 8px;
    }
    .active-badge {
        font-size: 10px;
        color: #2e8b57;
        background: #e6f7ec;
        padding: 2px 6px;
        border-radius: 4px;
        font-weight: bold;
        text-transform: uppercase;
        margin-right: 8px;
    }
    .expires-text {
        font-size: 12px;
        color: #e53935;
        font-weight: 500;
        margin-right: 12px;
    }
    .modal-subtitle {
        font-size: 14px;
        color: #666;
        margin-bottom: 15px;
    }
    .document-list {
        margin-bottom: 20px;
        padding: 20px 28px;
        border-radius: 12px;
        border: 1px solid #ddd;
        background-color: #fff;
    }
    .card-title {
        font-size: 16px;
        font-weight: 400;
        color: $color-black;
        margin-bottom: 20px;
    }
    .document-card {
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: white;
        padding: 22px;
        border-radius: 8px;
        margin-bottom: 10px;
        border: 1px solid rgba(0, 0, 0, 0.1);
        &:hover {
            background-color: #f9fafb;
        }
    }
    .doc-icon {
        font-size: 24px;
    }
    .doc-info {
        display: flex;
        align-items: center;
        flex-direction: row;
        gap: 14px;
    }
    .doc-title {
        font-weight: 500;
        font-size: 14px;
        color: $color-black;
        margin-bottom: 4px;
    }
    .doc-meta {
        display: flex;
        align-items: center;
        gap: 16px;
        font-size: 12px;
        color: gray;
        &__item {
            display: flex;
            align-items: center;
            gap: 4px;
            white-space: nowrap;
        }
    }
    .doc-tag {
        background: oklch(0.95 0.0058 264.53);
        padding: 4px 8px;
        border-radius: 8px;
        font-size: 12px;
        font-weight: 500;
        color: #030213;
        cursor: pointer;
    }
    .doc-actions {
        display: flex;
        align-items: center;
        gap: 14px;
    }

    .doc-btns {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .doc-btn {
        width: 36px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 8px;
        cursor: pointer;
        color: $color-black;
        transition: background-color 0.2s ease;

        &:hover {
            background-color: #e5e7eb;
        }
        align-items: center;
        gap: 14px;
    }
    .doc-delete-icon {
        cursor: pointer;
        color: #e53e3e;
    }
    .delete-modal-body {
        padding: 8px 0 4px;
        p {
            margin-bottom: 24px;
            color: #4a5568;
            font-size: 14px;
            line-height: 1.5;
        }
    }
    .shared-link-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 12px;
        padding: 10px 0;
        border-bottom: 1px solid #eee;
    }

    .share-modal-body {
        padding: 8px 0 4px;
    }
    .share-link-box {
        margin-bottom: 16px;
    }
    .detail-view {
        background: white;
        padding: 20px;
        border-radius: 12px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    }
    .detail-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
    .detail-header h3 {
        font-weight: 500;
        font-size: 14px;
        color: oklch(0.145 0 0);
    }
    .btn {
        padding: 4px 12px;
        color: oklch(25.723% 0.00149 16.173);
        border-radius: 8px;
        font-weight: 500;
        font-size: 13px;
        border: 1px solid #d1d5db;
        background: transparent;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 14px;
    }
    .detail-body {
        display: flex;
        gap: 20px;
        margin-top: 20px;
    }
    .preview-box {
        flex: 2;
        border: 1px solid #ddd;
        border-radius: 8px;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: flex-start;
        padding: 0;
        background: #ffffff;
        text-align: left;
        overflow: hidden;
        min-height: 650px;
    }
    .preview-box .loading-spinner {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        min-height: 650px;
        width: 100%;
    }
    .preview-placeholder {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 40px;
        height: 100%;
        min-height: 650px;
        background: #f8f9fa;
        width: 100%;
        box-sizing: border-box;
    }
    .preview-placeholder p {
        font-size: 16px;
        font-weight: 400;
        margin: 8px 10px;
    }
    .preview-placeholder span {
        font-size: 12px;
        font-weight: 400;
    }
    .doc-details {
        flex: 1;
    }
    .doc-details h4 {
        color: oklch(0.145 0 0);
        font-size: 16px;
        font-weight: 500;
        margin-bottom: 8px;
    }
    .doc-details p {
        color: oklch(0.145 0 0);
        font-size: 12px;
        margin-bottom: 9px;
    }
    .data {
        margin-right: 8px;
        color: oklch(0.446 0.03 256.802);
    }
    .status-tag {
        background: black;
        color: white;
        padding: 2px 6px;
        border-radius: 6px;
        font-size: 12px;
    }
    .back-btn {
        padding: 8px 14px;
        color: oklch(0.546 0.245 262.881);
        border-radius: 10px;
        font-weight: 500;
        font-size: 14px;
        background: oklch(0.932 0.032 255.585);
        cursor: pointer;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        transition: all 0.2s ease;
    }

    .back-btn:hover {
        background-color: oklch(0.546 0.245 262.881);
        color: white;
    }

    .back-btn:active {
        transform: scale(0.98);
    }

    .back-btn:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.4);
    }
    .tab-filter {
        display: flex;
    }
    @media screen and (max-width: 600px) {
        .document-search-list,
        .document-card,
        .detail-body {
            display: block !important;
        }
        .schedule-apply {
            margin-left: 3px !important;
            margin-top: 7px !important;
        }
    }
    .pdf-viewer {
        width: 100%;
        height: 650px;
        border: 1px solid #ddd;
        border-radius: 6px;
    }

    .image-viewer {
        max-width: 100%;
        max-height: 650px;
        object-fit: contain;
        border-radius: 6px;
    }

    .excel-preview-wrapper {
        width: 100%;
        height: 650px;
        display: flex;
        flex-direction: column;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        background-color: #ffffff;
        overflow: hidden;
    }

    .excel-header-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 12px;
        background-color: #f8fafc;
        border-bottom: 1px solid #e2e8f0;
        font-size: 12px;
        color: #64748b;
    }

    .excel-file-badge {
        display: flex;
        align-items: center;
        gap: 6px;
        font-weight: 600;
        color: #334155;
    }

    .excel-grid-meta {
        font-size: 11px;
        color: #94a3b8;
    }

    .excel-table-container {
        flex: 1;
        overflow: auto;
        position: relative;
        background-color: #ffffff;
    }

    .excel-table {
        border-collapse: collapse;
        table-layout: auto;
        min-width: 100%;
        font-size: 12px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    }

    .excel-corner-cell {
        position: sticky;
        top: 0;
        left: 0;
        z-index: 3;
        background-color: #f1f5f9;
        border-right: 1px solid #cbd5e1;
        border-bottom: 1px solid #cbd5e1;
        width: 44px;
        min-width: 44px;
    }

    .excel-col-header {
        position: sticky;
        top: 0;
        z-index: 2;
        background-color: #f1f5f9;
        color: #475569;
        font-weight: 600;
        text-align: center;
        padding: 6px 12px;
        border-right: 1px solid #e2e8f0;
        border-bottom: 1px solid #cbd5e1;
        min-width: 80px;
        user-select: none;
    }

    .excel-row-header {
        position: sticky;
        left: 0;
        z-index: 1;
        background-color: #f1f5f9;
        color: #64748b;
        font-weight: 600;
        text-align: center;
        padding: 4px 8px;
        border-right: 1px solid #cbd5e1;
        border-bottom: 1px solid #e2e8f0;
        width: 44px;
        min-width: 44px;
        user-select: none;
    }

    .excel-cell {
        padding: 6px 10px;
        border-right: 1px solid #e2e8f0;
        border-bottom: 1px solid #e2e8f0;
        color: #1e293b;
        white-space: nowrap;
        max-width: 250px;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .excel-table tbody tr:hover td {
        background-color: #f8fafc;
    }

    .excel-table tbody tr:hover .excel-row-header {
        background-color: #e2e8f0;
    }

    .excel-sheet-tabs {
        display: flex;
        gap: 2px;
        padding: 4px 8px 0;
        background-color: #f1f5f9;
        border-top: 1px solid #e2e8f0;
        overflow-x: auto;
    }

    .excel-sheet-tab {
        border: 1px solid transparent;
        border-bottom: none;
        background: transparent;
        padding: 5px 14px;
        font-size: 12px;
        font-weight: 500;
        color: #64748b;
        border-radius: 4px 4px 0 0;
        cursor: pointer;
        transition: all 0.15s ease;
    }

    .excel-sheet-tab:hover {
        color: #0f172a;
        background-color: rgba(255, 255, 255, 0.6);
    }

    .excel-sheet-tab--active {
        background-color: #ffffff !important;
        color: #16a34a !important;
        font-weight: 600;
        border-color: #cbd5e1;
        border-top: 2px solid #16a34a;
    }

    .text-preview-wrapper {
        width: 100%;
        height: 650px;
        display: flex;
        flex-direction: column;
        background-color: #ffffff;
        overflow: hidden;
    }

    .text-header-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 14px;
        background-color: #f8fafc;
        border-bottom: 1px solid #e2e8f0;
        font-size: 12px;
        color: #64748b;
    }

    .text-file-badge {
        display: flex;
        align-items: center;
        gap: 6px;
        font-weight: 600;
        color: #334155;
    }

    .text-header-actions {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .text-grid-meta {
        font-size: 11px;
        color: #94a3b8;
    }

    .text-editor-body {
        flex: 1;
        display: flex;
        overflow: auto;
        background-color: #ffffff;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
        font-size: 13px;
        line-height: 20px;
    }

    .text-line-numbers {
        padding: 12px 10px;
        background-color: #f8fafc;
        border-right: 1px solid #e2e8f0;
        color: #94a3b8;
        text-align: right;
        user-select: none;
        min-width: 44px;
        box-sizing: border-box;
    }

    .text-line-number {
        height: 20px;
        line-height: 20px;
    }

    .text-editor-content {
        flex: 1;
        margin: 0;
        padding: 12px 16px;
        font-family: inherit;
        font-size: inherit;
        line-height: inherit;
        color: #1e293b;
        background: transparent;
        white-space: pre;
        tab-size: 4;
        text-align: left;
        overflow: visible;
    }
    .card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .select-all {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
        font-weight: 600;
        color: #374151;
        cursor: pointer;
        margin-right: 12px;
    }

    .select-all input {
        cursor: pointer;
        height: 14px;
        width: 14px;
    }

    .edit-download {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 2px;
    }
</style>
